import { toast } from '@heroui/react'
import { create } from 'zustand'

import { reportsTimeApi } from '@/api/reportsTime.api'
import i18n from '@/i18n'
import type { TimeReport } from '@/types/timeReport'
import { getAppSettings, updateAppSettings } from '@/utils/settings'
import { SortDirection, type TableSort } from '@/utils/sorting'

interface TimesheetListStore {
  reports: TimeReport[];
  isLoading: boolean;
  error: string | null;
  filters: {
    driverID: string;
    year?: number;
    month?: number;
  };
  pagination: {
    sort: TableSort | null;
    page: number;
    limit: number;
    totalPages: number;
    total: number;
  };

  fetchReports: () => Promise<void>;
  deleteReport: (id: string) => Promise<void>;

  setFilters: (filters: Partial<TimesheetListStore['filters']>) => void;
  setPagination: (pagination: Partial<TimesheetListStore['pagination']>) => void;
  resetFilters: () => void;
}

export const useTimesheetListStore = create<TimesheetListStore>((set, get) => ({
  reports: [],
  isLoading: false,
  error: null,
  filters: {
    driverID: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  },
  pagination: {
    sort: { key: 'Year', direction: SortDirection.DESC },
    page: 0,
    limit: getAppSettings().reportsLimit ?? 10,
    totalPages: 0,
    total: 0,
  },

  fetchReports: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params = {
      driverID: filters.driverID || undefined,
      year: filters.year || undefined,
      month: filters.month || undefined,
      page: pagination.page,
      limit: pagination.limit,
    }

    try {
      const response = await reportsTimeApi.getTimeReports(params)
      set({
        reports: response.Reports || [],
        pagination: {
          ...pagination,
          totalPages: Math.ceil((response.Pagination?.Total || 0) / pagination.limit),
          total: response.Pagination?.Total || 0,
        },
        isLoading: false,
      })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message, isLoading: false })
      toast.danger(i18n.t('timesheet.toast.fetch_error'), { description: message })
    }
  },

  deleteReport: async id => {
    set({ isLoading: true, error: null })
    try {
      await reportsTimeApi.deleteTimeReport(id)
      toast.success(i18n.t('timesheet.toast.delete_success'))
      await get().fetchReports()
    } catch (err) {
      toast.danger(i18n.t('timesheet.toast.delete_error'), { description: (err as Error).message })
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchReports()
  },

  setPagination: newPagination => {
    set(state => {
      const updatedPagination = { ...state.pagination, ...newPagination }
      if (newPagination.limit !== undefined) {
        updateAppSettings({ reportsLimit: newPagination.limit })
      }
      return { pagination: updatedPagination }
    })
    get().fetchReports()
  },

  resetFilters: () => {
    const initialLimit = getAppSettings().reportsLimit ?? 10
    set({
      filters: {
        driverID: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      },
      pagination: {
        sort: { key: 'Year', direction: SortDirection.DESC },
        page: 0,
        limit: initialLimit,
        totalPages: 1,
        total: 0,
      },
    })
    get().fetchReports()
  },
}))
